import { _ as __decorate, f as __metadata, B as BaseComponent, c as TIMELINE_POINT_COLORS } from '../types-H7O1ogf7.js';
import { css, html } from 'lit';
import { property, customElement } from 'lit/decorators.js';
import 'lit/directive.js';

var styles = css`*,
*::before,
*::after {
  box-sizing: border-box;
  padding: 0;
  margin: 0;
}

:host {
  position: relative;
  display: flex;
  flex-wrap: var(--it-timeline-point-content-flex-wrap, nowrap);
  align-items: center;
  margin-bottom: var(--it-timeline-point-margin-bottom, 24px);
  font-family: "Titillium Web", "Titillium Sans Pro", Geneva, Tahoma, sans-serif;
  font-size: var(--bsi-body-font-size);
  line-height: var(--bsi-body-leading);
  list-style: none;
}
:host::before {
  position: absolute;
  z-index: 1;
  top: var(--it-timeline-point-stack-mobile-after-top, 0);
  left: 2rem;
  width: 2px;
  height: calc(100% + 24px);
  background-color: var(--point-color, var(--bsi-color-text-primary));
  content: var(--it-timeline-point-stack-mobile-after-content, none);
}

:host([color=primary]) {
  --point-color: var(--bsi-color-text-primary);
}

:host([color=secondary]) {
  --point-color: var(--bsi-color-text-secondary);
}

:host([color=success]) {
  --point-color: var(--bsi-color-text-success);
}

:host([color=info]) {
  --point-color: var(--bsi-color-text-info);
}

:host([color=warning]) {
  --point-color: var(--bsi-color-text-warning);
}

:host([color=danger]) {
  --point-color: var(--bsi-color-text-danger);
}

:host([color=light]) {
  --point-color: var(--bsi-color-text-light);
}

:host([color=dark]) {
  --point-color: var(--bsi-color-text-dark);
}

:host([color=black]) {
  --point-color: var(--bsi-color-text-black);
}

:host([color=white]) {
  --point-color: var(--bsi-color-text-white);
}

:host([align-top]) {
  align-items: flex-start;
}
:host([align-top]) .point-aside {
  align-self: stretch;
  justify-content: flex-start;
  padding-top: 0.5rem;
}
:host([align-top]) .point-aside::after {
  top: 2.6rem;
  transform: translateY(-5px);
}

:host([compact]) .point-aside {
  width: 3.5rem;
  padding: 0.5rem 0.75rem 0.5rem 0;
}
@media (max-width: 767.98px) {
  :host([compact]) .point-aside {
    width: 3rem;
    padding: 0.5rem 0.5rem 0.5rem 0;
  }
}

:host([has-point-top][align-top]) .point-aside::after {
  top: 3.5rem;
}
@media (max-width: 767.98px) {
  :host([has-point-top][align-top]) .point-aside::after {
    top: 2.7rem;
  }
}

.point-aside {
  position: relative;
  display: flex;
  width: 5rem;
  flex-direction: column;
  flex-shrink: 0;
  align-self: stretch;
  justify-content: center;
  padding: 0.5rem 1rem 0.5rem 0;
  color: var(--point-color, var(--bsi-color-text-primary));
  text-align: center;
}
@media (max-width: 767.98px) {
  .point-aside {
    width: 4rem;
    padding: 0.5rem 0.75rem 0.5rem 0;
  }
}
.point-aside::before {
  position: absolute;
  z-index: 1;
  top: 0;
  right: 0;
  width: 2px;
  height: calc(100% + 24px);
  background-color: var(--point-color, var(--bsi-color-text-primary));
  content: var(--it-timeline-point-after-content, "");
}
.point-aside::after {
  position: absolute;
  z-index: 2;
  top: 50%;
  right: -4px;
  width: 9px;
  height: 9px;
  border-radius: 50%;
  background-color: var(--point-color, var(--bsi-color-text-primary));
  content: "";
  transform: translateY(-50%);
}

:host(:last-child) .point-aside::before {
  height: 100%;
}

@media (max-width: 767.98px) {
  :host([stack-mobile]) .point-aside {
    width: auto;
    flex-direction: row-reverse;
    padding-right: 0.5rem;
    padding-left: 2.5rem;
  }
  :host([stack-mobile]) .point-aside::before {
    display: none;
  }
  :host([stack-mobile]) .point-aside::after {
    top: 50%;
    right: auto;
    left: 1.75rem;
    transform: translateY(-50%);
  }
  :host([stack-mobile]) .point-content {
    flex: 1 1 100%;
    margin-top: 16px;
    margin-left: 0;
  }
  :host([stack-mobile][align-top]) .point-aside::after {
    top: 2.6rem;
    right: auto;
    left: 1.75rem;
    transform: translateY(-5px);
  }
}
.point-content {
  z-index: var(--it-timeline-point-content-z-index, 1);
  min-width: 0;
  flex: 1 1 var(--it-timeline-point-content-flex-basis, 0) !important;
  margin-top: var(--it-timeline-point-margin-top, 0);
  margin-left: 1rem;
}
.point-content ::slotted(*:last-child) {
  margin-bottom: 0 !important;
}

::slotted([slot=date]),
::slotted([slot=milestone]),
::slotted([slot=content]) {
  display: block;
}

::slotted([slot=content]) *:last-child {
  margin-bottom: 0 !important;
}`;

/**
 * Singolo punto della timeline point list di Bootstrap Italia.
 *
 * Usa **shadow DOM** con slot reali.
 *
 * @slot date - Contenuto laterale per date: usa `<time datetime="...">`.
 *   Al suo interno puoi usare `<span class="point-visual">`, `<span class="point-top">`,
 *   `<span class="point-main">`, `<span class="point-bottom">`.
 * @slot milestone - Contenuto laterale alternativo per milestone/step.
 * @slot content - Contenuto principale del punto.
 *
 * @attr {string} color - Colore: `primary` (default), `secondary`, `success`, `danger`, `warning`, `info`, `light`, `dark`.
 * @attr {boolean} align-top - Fissa verticalmente il punto all'inizio del contenuto.
 * @attr {boolean} compact - Sezione laterale più compatta.
 *
 * @example Date con giorno e mese
 * ```html
 * <it-timeline-point>
 *   <time slot="date" datetime="2025-10-14">
 *     <span class="point-visual" aria-hidden="true">
 *       <span class="point-main">14</span>
 *       <span class="point-bottom">ott</span>
 *     </span>
 *   </time>
 *   <p slot="content">Lorem ipsum dolor sit amet.</p>
 * </it-timeline-point>
 * ```
 *
 * @example Milestone con icona
 * ```html
 * <it-timeline-point color="success">
 *   <div slot="milestone">
 *     <span class="visually-hidden">Milestone completata</span>
 *     <span class="point-visual" aria-hidden="true">
 *       <span class="point-main">
 *         <it-icon name="it-check-circle" aria-hidden="true"></it-icon>
 *       </span>
 *     </span>
 *   </div>
 *   <p slot="content">Lorem ipsum dolor sit amet.</p>
 * </it-timeline-point>
 * ```
 */
let ItTimelinePoint = class ItTimelinePoint extends BaseComponent {
    constructor() {
        super(...arguments);
        this.color = 'primary';
        this.alignTop = false;
        this.compact = false;
        /**
         * `true` se l'utente ha impostato esplicitamente l'attributo `color` in HTML.
         * Usato da `<it-timeline>` per decidere se propagare il colore del contenitore.
         */
        this._hasExplicitColor = false;
        /**
         * `true` se l'utente ha impostato esplicitamente l'attributo `compact` in HTML.
         * Usato da `<it-timeline>` per decidere se propagare il compact del contenitore.
         */
        this._hasExplicitCompact = false;
    }
    connectedCallback() {
        if (!this.hasAttribute('role')) {
            this.setAttribute('role', 'listitem');
        }
        // Detect explicit color BEFORE Lit reflects the default value
        this._hasExplicitColor = this.hasAttribute('color');
        this._hasExplicitCompact = this.hasAttribute('compact');
        // Get all icons slotted in slot pin-icon and set their size to "sm" (BSI default for timeline icons)
        const icons = document.querySelectorAll('it-icon');
        icons.forEach((icon) => {
            icon.setAttribute('size', 'sm');
        });
        super.connectedCallback?.();
    }
    updated(_changedProperties) {
        super.updated(_changedProperties);
        if (this.color && !TIMELINE_POINT_COLORS.includes(this.color)) {
            this.logger.warn(`Invalid color value, falling back to "primary". Expected one of: ${TIMELINE_POINT_COLORS.join(', ')}`);
        }
        else {
            // Recupera eventuali icone figlie slottate e aggiorna il loro colore per riflettere il colore di questo elemento
            const color = this.getColor();
            const icons = this.querySelectorAll('it-icon');
            icons.forEach((icon) => {
                if (icon.getAttribute('color') === 'inherit' || !icon.hasAttribute('color')) {
                    icon.setAttribute('color', color);
                }
            });
        }
    }
    getColor() {
        return TIMELINE_POINT_COLORS.includes(this.color) ? this.color : 'primary';
    }
    /**
     * Inspect the light DOM inside the slotted date/milestone elements and
     * set boolean host attributes `has-point-top` / `has-point-bottom` so that
     * shadow-DOM CSS can use reliable attribute selectors instead of the
     * browser-inconsistent `:host(:has(.point-top))` slotted-descendant form.
     */
    _updatePointSpanAttrs() {
        const hasTop = !!this.querySelector('.point-top, .point-year');
        const hasBottom = !!this.querySelector('.point-bottom, .point-month');
        this.toggleAttribute('has-point-top', hasTop);
        this.toggleAttribute('has-point-bottom', hasBottom);
    }
    /**
     * Ensure every `.point-visual` element inside slotted content carries
     * `aria-hidden="true"` automatically — the accessible label should come
     * from a sibling `<span class="visually-hidden">`.
     */
    _updatePointVisualAriaHidden() {
        this.querySelectorAll('.point-visual').forEach((el) => {
            el.setAttribute('aria-hidden', 'true');
        });
    }
    _onAsideSlotChange() {
        this._updatePointSpanAttrs();
        this._updatePointVisualAriaHidden();
    }
    render() {
        return html `
      <div class="point-aside">
        <slot name="date" @slotchange=${this._onAsideSlotChange}></slot>
        <slot name="milestone" @slotchange=${this._onAsideSlotChange}></slot>
      </div>
      <div class="point-content">
        <slot name="content"></slot>
      </div>
    `;
    }
};
ItTimelinePoint.styles = styles;
__decorate([
    property({ type: String, reflect: true }),
    __metadata("design:type", String)
], ItTimelinePoint.prototype, "color", void 0);
__decorate([
    property({ type: Boolean, attribute: 'align-top', reflect: true }),
    __metadata("design:type", Object)
], ItTimelinePoint.prototype, "alignTop", void 0);
__decorate([
    property({ type: Boolean, reflect: true }),
    __metadata("design:type", Object)
], ItTimelinePoint.prototype, "compact", void 0);
ItTimelinePoint = __decorate([
    customElement('it-timeline-point')
], ItTimelinePoint);

export { ItTimelinePoint };
//# sourceMappingURL=it-timeline-point.js.map
